import { useEffect, useCallback, FormEvent, useState } from 'react';
import {
    IAllRegistrationData,
    ICountryData,
    IPreferredLanguagesList,
    DefaultPageProps,
    IAllAccountData,
    IWalletPersonalData,
} from '@/types/types';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { dictionaryService } from '@/services/api';
import {
    createSelectOptionsFromArray,
    generateCountriesSelectOptions,
    generateHowHeardSelectOptions,
    generateProfessionsSelectOptions,
    generateTimezonesSelectOptions,
} from '@/utils/forms';
import { useConfigStore } from '@/store';
import { useThemeStore } from '@/store/themeStore';

interface UsePersonalDetailsProps {
    dictionary: DefaultPageProps['dictionary'];
    registrationData?: Partial<IAllRegistrationData>;
    accountData?: IAllAccountData;
    nonMemberPersonalData?: Partial<IWalletPersonalData>;
    restrictCountries?: boolean;
}
export const usePersonalDetails = ({
    dictionary,
    registrationData,
    accountData,
    nonMemberPersonalData,
    restrictCountries,
}: UsePersonalDetailsProps) => {
    const {
        professions: professionsData,
        howHeardList: howHeardData,
        timezones: timeZonesData,
    } = dictionary;

    const countriesData = restrictCountries
        ? dictionary?.countries?.filter((item) => item.isRegistrationOpen)
        : dictionary?.countries;

    const disabledProfessionIds = useConfigStore(
        (s) => s.website_registration_disabled_profession_ids,
    );

    const { t } = usePhrase();

    const { variables } = useThemeStore();

    const generatePreferredLanguagesSelectOptions = (
        countriesPassedData?: ICountryData[],
    ): IPreferredLanguagesList => {
        const createPreferredLanguagesSelectOptionsForCountry = (
            preferredLanguagesList: IPreferredLanguagesList,
            countryData: ICountryData,
        ): IPreferredLanguagesList => {
            return countryData.preferredLanguages && countryData.preferredLanguages.length
                ? {
                      ...preferredLanguagesList,
                      [countryData.countryCode]: countryData.preferredLanguages.map(
                          (preferredLanguage) => ({
                              label: preferredLanguage.phraseId,
                              value: preferredLanguage.languageId,
                          }),
                      ),
                  }
                : preferredLanguagesList;
        };

        const getPreferredLanguagesSelectOptionsForCountries = (
            countriesList: ICountryData[],
        ): IPreferredLanguagesList =>
            countriesList.reduce(createPreferredLanguagesSelectOptionsForCountry, {});

        const countriesList = countriesData || countriesPassedData;
        if (countriesList && Array.isArray(countriesList)) {
            return getPreferredLanguagesSelectOptionsForCountries(countriesList);
        } else {
            return {};
        }
    };

    const generateStatesSelectOptions = async (countryCode?: string) => {
        if (!countryCode) return [];

        let states = [];
        try {
            states = await dictionaryService.getStates(countryCode);
        } catch (e) {
            return [];
        }
        return createSelectOptionsFromArray({ label: 'name', value: 'stateCode' })(states);
    };

    const preferredList = generatePreferredLanguagesSelectOptions();

    const [preferredLangList, setPreferredLangList] = useState<
        { label: number; value: number }[] | null
    >(null);
    const [statesList, setStatesList] = useState<{ label: string; value: string }[] | null>();

    const setCurrentPreferred = useCallback(
        (
            val: string | FormEvent<HTMLDivElement>,
            preferredPassedList?: IPreferredLanguagesList,
        ) => {
            setPreferredLangList(null);
            if (!val) return;
            const currentVal = String(val);
            const preferredLanguages = preferredPassedList || preferredList;
            if (currentVal in preferredLanguages && preferredLanguages[currentVal].length) {
                setPreferredLangList(preferredLanguages[currentVal]);
            } else {
                setPreferredLangList(null);
            }
        },
        [preferredList],
    );

    const checkPreferred = async (
        val: string | FormEvent<HTMLDivElement>,
        preferredPassedList?: IPreferredLanguagesList,
    ) => {
        if (!val) return;
        const currentVal = String(val);
        setCurrentPreferred(currentVal, preferredPassedList);
        if (accountData) {
            const states = await generateStatesSelectOptions(currentVal);
            setStatesList(states);
        }
    };

    useEffect(() => {
        const getInitialStates = async () => {
            const states = await generateStatesSelectOptions(accountData?.countryCode);
            setStatesList(states);
        };
        if (accountData) {
            void getInitialStates();
        }
    }, [accountData]);

    useEffect(() => {
        const countryCode =
            accountData?.countryCode ||
            registrationData?.countryCode ||
            nonMemberPersonalData?.countryCode;
        if (countryCode) {
            setCurrentPreferred(countryCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountData, registrationData, nonMemberPersonalData]);

    return {
        countriesList: variables?.sortOrder?.countriesSelectOptions
            ? generateCountriesSelectOptions(
                  countriesData,
                  t,
                  variables.sortOrder.countriesSelectOptions,
              )
            : [],
        professionsList: generateProfessionsSelectOptions(
            professionsData,
            disabledProfessionIds,
            t,
            variables.sortOrder.professionalSelectOptions,
            registrationData?.countryCode || accountData?.countryCode,
        ),
        getCountryFilteredProfessionsList: (countryCode?: string) =>
            generateProfessionsSelectOptions(
                professionsData,
                disabledProfessionIds,
                t,
                variables.sortOrder.professionalSelectOptions,
                countryCode,
            ),
        howHeardList: generateHowHeardSelectOptions(
            howHeardData,
            t,
            variables.sortOrder.howHeardSelectOptions,
        ),
        statesList,
        timezonesList: generateTimezonesSelectOptions(timeZonesData),
        preferredLangList,
        checkPreferred,
        countriesData,
        professionsData,
        howHeardData,
    };
};
